import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { Email } from 'types/types'
import request, { getAxiosRequestConfig } from 'utils/request'
import { Methods } from 'types/request'
import { useStoreSelector } from 'hooks/store'
import { useGetLanguage } from 'hooks/use-get-language'
import styles from './Contact.module.scss'
import { Banner, Heading, Form, TextArea, TextField, Button } from '@boltenergy-be/design-system'
import { useGetProducerContactQuery } from 'store/queries/cms-api'
import LoadingCard from 'components/loading-card/LoadingCard'
import Link from 'components/link/Link'
import { BOLT_EMAIL_CONTACT, BOLT_PHONE_CONTACT } from 'constants/constants'
import { PhoneAvailability } from 'pages/app/support/contact/components/phone-availibility/PhoneAvailability.tsx'
import { selectCurrentAccount, selectCurrentProduct, selectCurrentServiceContracts, selectIsProducerAccount } from 'store/contact/slice.ts'
import { zoomOnFace } from 'utils/cloudinary.ts'
import { PRODUCTS_CONFIG } from '@boltenergy-be/shared'

const Contact = () => {
  // REDUX STORE
  const { contact } = useStoreSelector((store) => store.contact)
  const isProducer = useStoreSelector(selectIsProducerAccount)
  const selectedAccount = useStoreSelector(selectCurrentAccount)
  const { electricity } = useStoreSelector(selectCurrentServiceContracts)
  const currentProduct = useStoreSelector(selectCurrentProduct)
  const language = useGetLanguage()

  // Redux Queries
  const { data, isLoading } = useGetProducerContactQuery({ contactId: selectedAccount.ownerId || '' }, { skip: !isProducer })

  // React Hooks
  const { t } = useTranslation(['support', 'validation', 'errors'])
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<Email>()

  // Local state
  const [status, setStatus] = useState<'submitted' | 'failed' | 'sending' | null>(null)

  // Constants
  const showProducerInfo = isProducer && !!data?.contact
  const contactPhone = showProducerInfo ? data.contact.phone : BOLT_PHONE_CONTACT
  const contactEmail = showProducerInfo ? data.contact.email : BOLT_EMAIL_CONTACT[language]
  const showPhone = showProducerInfo || (currentProduct ? !PRODUCTS_CONFIG[currentProduct].digitalOnly.contactOptions : true)

  /**
   * Handles the on submit after valdiation
   *
   * @param data
   */
  const onSubmit = async (data: Email) => {
    setStatus('sending')
    const { subject, message } = data
    const from: string = contact?.email || ''
    const deliveryAddress = electricity.deliveryPoint.address

    try {
      const { success } = await request(
        getAxiosRequestConfig(Methods.POST, '/contact/support', { email: from, subject, message, deliveryAddress })
      )

      if (success) {
        setStatus('submitted')
      } else {
        setStatus('failed')
      }
    } catch (err) {
      setStatus('failed')
    }
  }

  return isLoading ? (
    <LoadingCard />
  ) : (
    <div className={styles['contact-container']}>
      <img
        src={
          showProducerInfo && data.contact.avatar
            ? zoomOnFace(data.contact.avatar.url)
            : 'https://res.cloudinary.com/bolt-energie/image/upload/v1705664314/website/common/customer-care.png'
        }
        alt={showProducerInfo && data.contact.avatar ? data.contact.firstName : 'Our support team'}
        className={styles.image}
      />

      <div className={styles.information}>
        <section className={styles.contact}>
          {showProducerInfo && (
            <Heading as="h2" variant="h5">
              {data?.contact.firstName} {data?.contact.lastName}
            </Heading>
          )}
          <p className="text-lg">
            {showProducerInfo
              ? t(($) => $.contact.contactPerson, {
                  name: data?.contact.firstName
                })
              : t(($) => $.contact.team)}
          </p>
          <ul className={styles['contact-cards']}>
            <li>
              <Link
                representation="button"
                isFullwidth
                variant="secondary"
                className={styles['contact-btn']}
                href={`mailto:${contactEmail}`}
              >
                <strong>{contactEmail}</strong>
                <small>{t(($) => $.contact.emailAvailability)}</small>
              </Link>
            </li>
            {showPhone && (
              <li>
                <Link
                  representation="button"
                  isFullwidth
                  variant="secondary"
                  className={styles['contact-btn']}
                  href={`tel:${contactPhone}`}
                >
                  <strong>{contactPhone}</strong>
                  <small>
                    <PhoneAvailability />
                  </small>
                </Link>
              </li>
            )}
          </ul>
        </section>

        <Form onSubmit={handleSubmit(onSubmit)} className={styles['contact-form']}>
          <Heading as="h2" variant="h5" weight="regular">
            {t(($) => $.contact.orMessage)}
          </Heading>

          <TextField
            label={t(($) => $.contact.subject)}
            id="subject"
            {...register('subject', {
              required: t(($) => $.required, {
                ns: 'validation'
              })
            })}
            maxLength={255}
            grid="full"
            error={errors?.subject?.type === 'required'}
          />

          <TextArea
            label={t(($) => $.contact.message)}
            id="message"
            {...register('message', {
              required: t(($) => $.required, {
                ns: 'validation'
              })
            })}
            grid="full"
            error={errors?.subject?.type === 'required'}
          />

          <Button disabled={!!status} loading={status === 'sending'} isFullwidth>
            {t(($) => $.contact.send)}
          </Button>
        </Form>

        {(status === 'submitted' || status === 'failed') && (
          <Banner type={status === 'failed' ? 'blocking' : 'positive'}>
            {status === 'submitted' && t(($) => $.contact.messageSent)}
            {status === 'failed' &&
              t(($) => $.default, {
                ns: 'errors'
              })}
          </Banner>
        )}
      </div>
    </div>
  )
}

export default Contact
